import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, Plus, Send, Users, ArrowLeft, Check, CheckCheck, Loader2, Search, X, Paperclip, FileText, Download, Smile, MoreVertical, Trash2, Ban, LogOut, Megaphone, History, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GROUP_WIDE_ROLES, SOCIAL_LIFE_ROLES } from "../lib/constants";
import Avatar from "../components/Avatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const fullUrl = (u) => (u ? (u.startsWith("http") ? u : BACKEND + u) : "");
const STATUS_COLOR = { available: "#10b981", busy: "#f59e0b", away: "#94a3b8" };
const MANAGE_ROLES = ["admin", "directeur", "directeur_adjoint"];
const EMOJIS = ["😀","😄","😉","😊","😍","😘","😂","🤣","🙂","😌","😎","🤗","🤔","😴","😢","😭","😡","😱","👍","👎","👏","🙏","💪","🙌","👋","🤝","❤️","🧡","💛","💚","💙","💜","🔥","✨","🎉","🎊","✅","❌","⚠️","💯","🥳","😅","😇","🤩","😋","😬","🫶","👀"];
const QUICK_REACTIONS = ["👍","❤️","😂","😮","😢","🙏"];
const fmt = (iso) => { try { return new Date(iso).toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString("fr-BE", { weekday: "short", day: "2-digit", month: "short" }); } catch { return ""; } };

const AvatarWithStatus = ({ url, name, size, online, status = "available" }) => {
  const { t: tt } = useTranslation();
  return (
  <div className="relative shrink-0">
    <Avatar url={url} name={name} size={size} />
    {online && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-[#0b1620]" style={{ background: STATUS_COLOR[status] || STATUS_COLOR.available }} data-testid="online-dot" title={status === "busy" ? tt("messagerie.busy") : status === "away" ? tt("messagerie.away") : tt("messagerie.online")} />}
  </div>
  );
};

export default function Messagerie() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState([]);
  const [text, setText] = useState("");
  const [contacts, setContacts] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [search, setSearch] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  // Suppressions : { kind: 'message'|'conversation', id, reasonNeeded } | null
  const [confirmDel, setConfirmDel] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  const lastTypingRef = useRef(0);
  activeRef.current = active;

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/planning/chat/conversations");
      const seen = new Set();
      const uniq = (data.conversations || []).filter((c) => (seen.has(c.id) ? false : seen.add(c.id)));
      setConvs(uniq);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (cid, markRead = false) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/planning/chat/conversations/${cid}/messages`);
      if (data.conversation_deleted) {
        setActive(null); setMessages([]); setSearchParams({});
        loadConvs();
        return;
      }
      setMessages(data.messages || []);
      setTyping(data.typing || []);
      if (markRead) { await api.post(`/planning/chat/conversations/${cid}/read`); loadConvs(); }
    } catch {}
  }, [loadConvs, setSearchParams]);

  useEffect(() => { loadConvs(); api.get("/planning/chat/contacts").then(({ data }) => setContacts(data.contacts || [])).catch(() => {}); }, [loadConvs]);
  useEffect(() => { const t = setInterval(loadConvs, 5000); return () => clearInterval(t); }, [loadConvs]);
  useEffect(() => {
    if (!active) return;
    loadMessages(active.id, true);
    const t = setInterval(() => loadMessages(activeRef.current?.id, true), 2500);
    return () => clearInterval(t);
  }, [active, loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // deep-link ?c=<id>
  useEffect(() => {
    const cid = searchParams.get("c");
    if (cid && convs.length && (!active || active.id !== cid)) {
      const c = convs.find((x) => x.id === cid);
      if (c) setActive(c);
    }
    // eslint-disable-next-line
  }, [convs]);

  const openConv = (c) => { setActive(c); setSearchParams({ c: c.id }); };

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setText("");
    try {
      await api.post(`/planning/chat/conversations/${active.id}/messages`, { text: t });
      await loadMessages(active.id, true);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setText(t); }
  };

  const insertEmoji = (e) => { setText((tx) => tx + e); setEmojiOpen(false); };

  // Appel vidéo (Jitsi Meet intégré)
  const [callRoom, setCallRoom] = useState(null);
  const joinCall = (room) => setCallRoom(room);
  const startCall = async () => {
    if (!active) return;
    const room = `CarePilot-${active.id}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      await api.post(`/planning/chat/conversations/${active.id}/messages`, { text: t("messagerie.videoStarted"), call_room: room });
      loadMessages(active.id);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setCallRoom(room);
  };
  const jitsiUrl = callRoom
    ? `https://meet.jit.si/${callRoom}#userInfo.displayName=${encodeURIComponent(JSON.stringify(user?.name || "Utilisateur"))}&config.prejoinConfig.enabled=false&config.prejoinPageEnabled=false`
    : "";
  // Deep-link ?call=<room> (depuis la bannière d'appel entrant) -> ouvre la visio
  useEffect(() => {
    const room = searchParams.get("call");
    if (room) {
      setCallRoom(room);
      const c = searchParams.get("c");
      setSearchParams(c ? { c } : {});
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const reactMessage = async (m, emoji) => {
    try {
      await api.post(`/planning/chat/messages/${m.id}/react`, { emoji });
      await loadMessages(active.id);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const leaveGroup = async (c) => {
    try {
      await api.post(`/planning/chat/conversations/${c.id}/leave`);
      if (active?.id === c.id) { setActive(null); setSearchParams({}); }
      await loadConvs();
      toast.success(t("messagerie.leftGroupToast"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const askLeaveGroup = (c) => setConfirmDel({ kind: "leave", id: c.id, conv: c });

  const canModerate = MANAGE_ROLES.includes(user?.role);
  const isGroupWide = GROUP_WIDE_ROLES.includes(user?.role);
  const isSocialLife = SOCIAL_LIFE_ROLES.includes(user?.role);
  const [maisonFilter, setMaisonFilter] = useState("all");
  const [annOpen, setAnnOpen] = useState(false);
  const [annText, setAnnText] = useState("");
  const [annBusy, setAnnBusy] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const openHist = async () => {
    setHistOpen(true); setHistLoading(true);
    try { const { data } = await api.get("/planning/chat/broadcasts"); setHist(data.broadcasts || []); } catch {}
    setHistLoading(false);
  };
  const maisonOptions = useMemo(() => {
    const seen = new Map();
    contacts.forEach((c) => { if (c.maison_id && !seen.has(c.maison_id)) seen.set(c.maison_id, c.maison || c.maison_id); });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [contacts]);
  const dialogContacts = useMemo(() => (maisonFilter === "all" ? contacts : contacts.filter((c) => c.maison_id === maisonFilter)), [contacts, maisonFilter]);
  const selectAllContacts = () => setPicked((p) => {
    const ids = dialogContacts.map((u) => u.username);
    const allOn = ids.every((u) => p.includes(u));
    return allOn ? p.filter((u) => !ids.includes(u)) : Array.from(new Set([...p, ...ids]));
  });
  const sendBroadcast = async () => {
    const txt = annText.trim();
    if (!txt) return;
    setAnnBusy(true);
    try {
      const { data } = await api.post("/planning/chat/broadcast", { text: txt });
      toast.success(t("messagerie.broadcastSent", { count: data.sent }));
      setAnnOpen(false); setAnnText(""); loadConvs();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setAnnBusy(false);
  };

  // Suppression d'un message : scope 'me' (immédiat) ou 'all' (confirmation)
  const deleteMessageForMe = async (m) => {
    try {
      await api.delete(`/planning/chat/messages/${m.id}`, { data: { scope: "me" } });
      await loadMessages(active.id);
      toast.success(t("messagerie.msgDeletedToast"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const askDeleteMessageForAll = (m) => {
    const reasonNeeded = canModerate && !m.mine;
    setDelReason("");
    setConfirmDel({ kind: "message", id: m.id, mine: m.mine, reasonNeeded });
  };
  const askDeleteConversationForAll = (c) => {
    setDelReason("");
    setConfirmDel({ kind: "conversation", id: c.id });
  };
  const deleteConversationForMe = async (c) => {
    try {
      await api.delete(`/planning/chat/conversations/${c.id}`, { data: { scope: "me" } });
      if (active?.id === c.id) { setActive(null); setSearchParams({}); }
      await loadConvs();
      toast.success(t("messagerie.convDeletedToast"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const confirmDeleteForAll = async () => {
    if (!confirmDel) return;
    if (confirmDel.kind === "leave") {
      await leaveGroup(confirmDel.conv);
      setConfirmDel(null); return;
    }
    if (confirmDel.kind === "message" && confirmDel.reasonNeeded && !delReason.trim()) {
      toast.error(t("messagerie.reasonRequired")); return;
    }
    try {
      if (confirmDel.kind === "message") {
        await api.delete(`/planning/chat/messages/${confirmDel.id}`, { data: { scope: "all", reason: delReason.trim() } });
        await loadMessages(active.id);
        toast.success(t("messagerie.msgDeletedToast"));
      } else {
        await api.delete(`/planning/chat/conversations/${confirmDel.id}`, { data: { scope: "all" } });
        if (active?.id === confirmDel.id) { setActive(null); setSearchParams({}); }
        await loadConvs();
        toast.success(t("messagerie.convDeletedToast"));
      }
      setConfirmDel(null); setDelReason("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const pingTyping = () => {
    if (!active) return;
    const now = Date.now();
    if (now - lastTypingRef.current < 2000) return;
    lastTypingRef.current = now;
    api.post(`/planning/chat/conversations/${active.id}/typing`).catch(() => {});
  };

  const fileRef = useRef(null);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f || !active) return;
    const okTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
    if (!okTypes.includes(f.type)) { toast.error(t("messagerie.formatError")); return; }
    if (f.size > 8_000_000) { toast.error(t("messagerie.fileTooBig")); return; }
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
    try {
      await api.post(`/planning/chat/conversations/${active.id}/messages`, { text: "", file_base64: b64, file_name: f.name, content_type: f.type });
      await loadMessages(active.id, true);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const startConversation = async () => {
    if (!picked.length) { toast.error(t("messagerie.selectAtLeastOne")); return; }
    const type = picked.length > 1 ? "group" : "direct";
    if (type === "group" && !groupName.trim()) { toast.error(t("messagerie.nameGroup")); return; }
    try {
      const { data } = await api.post("/planning/chat/conversations", { type, participants: picked, name: groupName.trim() });
      setNewOpen(false); setPicked([]); setGroupName("");
      await loadConvs();
      const { data: list } = await api.get("/planning/chat/conversations");
      setConvs(list.conversations || []);
      const c = (list.conversations || []).find((x) => x.id === data.id);
      if (c) openConv(c);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const filteredConvs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? convs.filter((c) => c.title.toLowerCase().includes(q)) : convs;
  }, [convs, search]);

  return (
    <div className="h-[calc(100vh-68px)] bg-[#0b1620] text-slate-100 -mx-4 -mb-4 sm:-mx-6 sm:-mb-6 flex" data-testid="messagerie-page">
      {/* Liste des conversations */}
      <aside className={`w-full sm:w-80 border-r border-white/10 flex flex-col ${active ? "hidden sm:flex" : "flex"}`}>
        <div className="p-4 border-b border-white/10">
          <h1 className="text-xl font-extrabold flex items-center gap-2 mb-3 min-w-0"><MessageCircle className="h-5 w-5 text-sky-400 shrink-0" /><span className="truncate">{t("messagerie.title")}</span></h1>
          <div className="flex items-center gap-2 mb-3">
            {isSocialLife && (
              <Button size="sm" variant="outline" onClick={() => setAnnOpen(true)} data-testid="chat-broadcast-btn" className="flex-1 min-w-0 h-9 border-teal-400/40 text-teal-300 hover:bg-teal-400/10"><Megaphone className="h-4 w-4 mr-1 shrink-0" /><span className="truncate">{t("messagerie.announce")}</span></Button>
            )}
            <Button size="sm" onClick={() => setNewOpen(true)} data-testid="chat-new-btn" className="flex-1 min-w-0 bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold h-9"><Plus className="h-4 w-4 mr-1 shrink-0" /><span className="truncate">{t("messagerie.new")}</span></Button>
            {isSocialLife && (
              <Button size="sm" variant="outline" onClick={openHist} data-testid="chat-hist-btn" title={t("messagerie.historyTitle")} className="h-9 w-9 p-0 shrink-0 border-white/15 text-slate-300 hover:bg-white/10"><History className="h-4 w-4" /></Button>
            )}
          </div>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("messagerie.search")} className="pl-9 h-9 bg-white/[0.04] border-white/10 text-slate-100" data-testid="chat-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="chat-conv-list">
          {filteredConvs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10 px-4">{t("messagerie.empty")}</p>
          ) : filteredConvs.map((c) => (
            <div key={c.id} data-testid={`chat-conv-${c.id}`}
              className={`group relative w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition ${active?.id === c.id ? "bg-white/[0.06]" : ""}`}>
              <button onClick={() => openConv(c)} data-testid={`chat-conv-open-${c.id}`} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                {c.type === "group"
                  ? <div className="h-11 w-11 rounded-full bg-violet-500/20 text-violet-300 flex items-center justify-center shrink-0"><Users className="h-5 w-5" /></div>
                  : <AvatarWithStatus url={c.avatar_url} name={c.title} size={44} online={c.online} status={c.status} />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold truncate">{c.title}</span>
                    <span className="text-[10px] text-slate-500 shrink-0">{c.last_at ? fmt(c.last_at) : ""}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-400 truncate">{c.last_text || (c.subtitle || "")}</span>
                    {c.unread > 0 && <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${c.id}`}>{c.unread}</span>}
                  </div>
                </div>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`chat-conv-menu-${c.id}`} onClick={(e) => e.stopPropagation()}
                    className="shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-white/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0f2130] border-white/10 text-slate-100">
                  <DropdownMenuItem data-testid={`chat-conv-del-me-${c.id}`} onClick={() => deleteConversationForMe(c)} className="cursor-pointer focus:bg-white/10">
                    <Trash2 className="h-4 w-4 mr-2" />{t("messagerie.deleteConvForMe")}
                  </DropdownMenuItem>
                  {c.type === "group" && (
                    <DropdownMenuItem data-testid={`chat-conv-leave-${c.id}`} onClick={() => askLeaveGroup(c)} className="cursor-pointer focus:bg-white/10">
                      <LogOut className="h-4 w-4 mr-2" />{t("messagerie.leaveGroup")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid={`chat-conv-del-all-${c.id}`} onClick={() => askDeleteConversationForAll(c)} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300">
                    <Ban className="h-4 w-4 mr-2" />{t("messagerie.deleteConvForEveryone")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      </aside>

      {/* Fil de discussion */}
      <section className={`flex-1 flex flex-col ${active ? "flex" : "hidden sm:flex"}`}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-3">
            <MessageCircle className="h-14 w-14 opacity-30" />
            <p>{t("messagerie.selectConv")}</p>
          </div>
        ) : (
          <>
            <div className="h-16 px-4 flex items-center gap-3 border-b border-white/10 shrink-0">
              <button className="sm:hidden text-slate-300" onClick={() => { setActive(null); setSearchParams({}); }} data-testid="chat-back"><ArrowLeft className="h-5 w-5" /></button>
              {active.type === "group"
                ? <div className="h-9 w-9 rounded-full bg-violet-500/20 text-violet-300 flex items-center justify-center"><Users className="h-4 w-4" /></div>
                : <AvatarWithStatus url={active.avatar_url} name={active.title} size={36} online={active.online} status={active.status} />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold truncate">{active.title}</div>
                <div className="text-xs text-slate-400 truncate">{active.type !== "group" && active.online ? t("messagerie.online") : active.subtitle}</div>
              </div>
              <button data-testid="chat-video-btn" onClick={startCall} title={t("messagerie.videoCall")}
                className="ml-auto shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-emerald-300 hover:text-emerald-200 hover:bg-emerald-400/10 transition">
                <Video className="h-5 w-5" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="chat-header-menu" className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-white/10 transition">
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0f2130] border-white/10 text-slate-100">
                  <DropdownMenuItem data-testid="chat-header-del-me" onClick={() => deleteConversationForMe(active)} className="cursor-pointer focus:bg-white/10">
                    <Trash2 className="h-4 w-4 mr-2" />{t("messagerie.deleteConvForMe")}
                  </DropdownMenuItem>
                  {active.type === "group" && (
                    <DropdownMenuItem data-testid="chat-header-leave" onClick={() => askLeaveGroup(active)} className="cursor-pointer focus:bg-white/10">
                      <LogOut className="h-4 w-4 mr-2" />{t("messagerie.leaveGroup")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="chat-header-del-all" onClick={() => askDeleteConversationForAll(active)} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300">
                    <Ban className="h-4 w-4 mr-2" />{t("messagerie.deleteConvForEveryone")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
              {messages.map((m, i) => (
                m.system ? (
                  <div key={m.id} className="flex justify-center my-2" data-testid={`chat-msg-system-${i}`}>
                    <span className="text-[11px] text-slate-400 bg-white/[0.05] rounded-full px-3 py-1">{m.text}</span>
                  </div>
                ) : (
                <div key={m.id} className={`group flex ${m.mine ? "justify-end" : "justify-start"} items-end gap-1.5`}>
                  {!m.mine && active.type === "group" && <Avatar url={m.avatar_url} name={m.sender_name} size={28} className="mb-1" />}
                  {m.mine && !m.deleted && (
                    <MsgActions align="end" onReact={(e) => reactMessage(m, e)} onDelMe={() => deleteMessageForMe(m)} onDelAll={() => askDeleteMessageForAll(m)} canDelAll={true} t={t} />
                  )}
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${m.deleted ? "bg-white/[0.05] text-slate-400 italic" : m.mine ? "bg-sky-500 text-white rounded-br-sm" : "bg-white/[0.08] text-slate-100 rounded-bl-sm"}`} data-testid={`chat-msg-${i}`}>
                    {!m.mine && active.type === "group" && !m.deleted && <div className="text-[10px] font-bold text-sky-300 mb-0.5">{m.sender_name}</div>}
                    {m.deleted ? (
                      <p className="text-sm flex items-center gap-1.5" data-testid={`chat-msg-deleted-${i}`}><Ban className="h-3.5 w-3.5 opacity-70" />{t("messagerie.messageDeleted")}</p>
                    ) : (
                      <>
                        {m.attachment && (m.attachment.kind === "image" ? (
                          <a href={fullUrl(m.attachment.url)} target="_blank" rel="noreferrer" data-testid={`chat-attach-${i}`}>
                            <img src={fullUrl(m.attachment.url)} alt={m.attachment.name} className="rounded-lg max-h-48 max-w-full mb-1 object-cover" />
                          </a>
                        ) : (
                          <a href={fullUrl(m.attachment.url)} target="_blank" rel="noreferrer" data-testid={`chat-attach-${i}`}
                            className={`flex items-center gap-2 rounded-lg px-2.5 py-2 mb-1 ${m.mine ? "bg-white/20" : "bg-black/20"}`}>
                            <FileText className="h-5 w-5 shrink-0" />
                            <span className="text-xs truncate flex-1">{m.attachment.name}</span>
                            <Download className="h-4 w-4 shrink-0 opacity-80" />
                          </a>
                        ))}
                        {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                        {m.call_room && (
                          <button onClick={() => joinCall(m.call_room)} data-testid={`chat-join-call-${i}`}
                            className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-900 transition">
                            <Video className="h-3.5 w-3.5" />{t("messagerie.joinCall")}
                          </button>
                        )}
                      </>
                    )}
                    <div className={`flex items-center gap-1 justify-end mt-0.5 text-[9px] ${m.mine ? "text-sky-100/80" : "text-slate-400"}`}>
                      {fmt(m.created_at)}
                      {m.mine && !m.deleted && (m.read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />)}
                    </div>
                    {!m.deleted && m.reactions && m.reactions.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`chat-reactions-${i}`}>
                        {m.reactions.map((r) => (
                          <button key={r.emoji} type="button" onClick={() => reactMessage(m, r.emoji)} data-testid={`chat-reaction-${i}-${r.emoji}`}
                            className={`text-[11px] leading-none rounded-full px-1.5 py-0.5 border transition ${r.mine ? "bg-sky-400/25 border-sky-300/50" : "bg-black/20 border-white/10 hover:bg-black/30"}`}>
                            {r.emoji} {r.count}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {!m.mine && !m.deleted && (
                    <MsgActions align="start" onReact={(e) => reactMessage(m, e)} onDelMe={() => deleteMessageForMe(m)} onDelAll={() => askDeleteMessageForAll(m)} canDelAll={canModerate} t={t} />
                  )}
                </div>
                )
              ))}
              <div ref={bottomRef} />
            </div>
            {typing.length > 0 && (
              <div className="px-4 pb-1 text-xs text-sky-300 flex items-center gap-1.5 shrink-0" data-testid="chat-typing">
                <span className="flex gap-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
                {typing.length === 1 ? t("messagerie.typingOne", { name: typing[0].name }) : t("messagerie.typingMany", { count: typing.length })}
              </div>
            )}
            <div className="p-3 border-t border-white/10 flex items-center gap-2 shrink-0">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="chat-file-input" />
              <Button onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn" variant="ghost" className="h-10 w-10 p-0 shrink-0 text-slate-300 hover:text-sky-300" title={t("messagerie.attachTitle")}><Paperclip className="h-5 w-5" /></Button>
              <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                <PopoverTrigger asChild>
                  <Button data-testid="chat-emoji-btn" variant="ghost" className="h-10 w-10 p-0 shrink-0 text-slate-300 hover:text-amber-300" title={t("messagerie.emojiTitle")}><Smile className="h-5 w-5" /></Button>
                </PopoverTrigger>
                <PopoverContent align="start" side="top" className="w-72 p-2 bg-[#0f2130] border-white/10" data-testid="chat-emoji-panel">
                  <div className="grid grid-cols-8 gap-1 max-h-52 overflow-y-auto">
                    {EMOJIS.map((e) => (
                      <button key={e} type="button" data-testid={`chat-emoji-${e}`} onClick={() => insertEmoji(e)}
                        className="h-8 w-8 rounded-lg text-xl leading-none flex items-center justify-center hover:bg-white/10 transition">{e}</button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Input value={text} onChange={(e) => { setText(e.target.value); pingTyping(); }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t("messagerie.writeMsg")} className="bg-white/[0.04] border-white/10 text-slate-100" data-testid="chat-input" />
              <Button onClick={send} disabled={!text.trim()} data-testid="chat-send" className="bg-sky-500 hover:bg-sky-400 text-slate-900 h-10 w-10 p-0 shrink-0"><Send className="h-4 w-4" /></Button>
            </div>
          </>
        )}
      </section>

      {/* Nouvelle discussion */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100" data-testid="chat-new-dialog">
          <DialogHeader><DialogTitle>{t("messagerie.newDialogTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {isGroupWide && maisonOptions.length > 1 && (
              <Select value={maisonFilter} onValueChange={setMaisonFilter}>
                <SelectTrigger className="bg-white/[0.04] border-white/10 text-slate-100 h-9" data-testid="chat-maison-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f2130] border-white/10 text-slate-100">
                  <SelectItem value="all">{t("messagerie.allHouses")}</SelectItem>
                  {maisonOptions.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {isGroupWide && dialogContacts.length > 0 && (
              <div className="flex items-center justify-between gap-2">
                <button type="button" data-testid="chat-select-all-group" onClick={selectAllContacts}
                  className="text-xs font-semibold text-teal-300 hover:text-teal-200 inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {dialogContacts.every((u) => picked.includes(u.username)) ? t("messagerie.unselectAll") : t("messagerie.selectAllGroup")}
                </button>
                <span className="text-[11px] text-slate-500">{picked.length}/{dialogContacts.length}</span>
              </div>
            )}
            {picked.length > 1 && (
              <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder={t("messagerie.groupName")} className="bg-white/[0.04] border-white/10 text-slate-100" data-testid="chat-group-name" />
            )}
            <div className="max-h-72 overflow-y-auto space-y-1" data-testid="chat-contacts">
              {dialogContacts.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">{t("messagerie.noColleague")}</p> : dialogContacts.map((u) => {
                const on = picked.includes(u.username);
                return (
                  <label key={u.username} className={`flex items-center gap-3 rounded-xl px-3 py-2 cursor-pointer transition ${on ? "bg-sky-500/15" : "hover:bg-white/5"}`} data-testid={`chat-contact-${u.username}`}>
                    <Checkbox checked={on} onCheckedChange={() => setPicked((p) => on ? p.filter((x) => x !== u.username) : [...p, u.username])} />
                    <AvatarWithStatus url={u.avatar_url} name={u.name} size={34} online={u.online} status={u.status} />
                    <div className="min-w-0"><div className="text-sm font-medium truncate">{u.name}</div><div className="text-xs text-slate-400 truncate">{u.role_label}</div></div>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-slate-400">{picked.length <= 1 ? t("messagerie.directHint") : t("messagerie.groupHint", { count: picked.length })}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>{t("messagerie.cancel")}</Button>
            <Button onClick={startConversation} data-testid="chat-start-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">{t("messagerie.start")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Annonce (diffusion à tout le personnel paramédical/animation du groupe) */}
      <Dialog open={annOpen} onOpenChange={setAnnOpen}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100" data-testid="chat-broadcast-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-teal-300" />{t("messagerie.announceTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-400">{t("messagerie.announceDesc")}</p>
            <Textarea value={annText} onChange={(e) => setAnnText(e.target.value)} data-testid="chat-broadcast-text"
              placeholder={t("messagerie.announcePlaceholder")} className="bg-white/[0.04] border-white/10 text-slate-100 min-h-[110px]" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAnnOpen(false)}>{t("messagerie.cancel")}</Button>
            <Button onClick={sendBroadcast} disabled={annBusy || !annText.trim()} data-testid="chat-broadcast-send" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold">
              {annBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Megaphone className="h-4 w-4 mr-1" />}{t("messagerie.announceSend")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historique des annonces + accusé de lecture */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100 max-w-lg" data-testid="chat-history-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-teal-300" />{t("messagerie.historyTitle")}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3">
            {histLoading ? (
              <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
            ) : hist.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="chat-history-empty">{t("messagerie.historyEmpty")}</p>
            ) : hist.map((b) => (
              <div key={b.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`chat-history-item-${b.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[11px] text-slate-400">{fmt(b.created_at)}</span>
                  <span className="text-[11px] font-semibold text-teal-300" data-testid={`chat-history-reads-${b.id}`}>
                    {t("messagerie.readBy", { read: b.read_count, total: b.total })}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap break-words mb-2">{b.text}</p>
                <div className="flex flex-wrap gap-1">
                  {b.recipients.map((r) => (
                    <span key={r.username} className={`text-[10px] rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${r.read ? "bg-emerald-500/15 text-emerald-300" : "bg-white/[0.06] text-slate-400"}`}>
                      {r.read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3 opacity-60" />}{r.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Appel vidéo Jitsi intégré */}
      <Dialog open={!!callRoom} onOpenChange={(o) => { if (!o) setCallRoom(null); }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[85vh] p-0 gap-0 bg-black border-white/10 overflow-hidden" data-testid="chat-video-dialog">
          <DialogHeader className="px-4 py-2 border-b border-white/10">
            <DialogTitle className="flex items-center gap-2 text-sm text-emerald-300"><Video className="h-4 w-4" />{t("messagerie.videoCall")}</DialogTitle>
          </DialogHeader>
          {callRoom && (
            <iframe title="visio" src={jitsiUrl} data-testid="chat-video-iframe"
              allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
              className="w-full border-0" style={{ height: "calc(85vh - 46px)" }} />
          )}
        </DialogContent>
      </Dialog>


      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) { setConfirmDel(null); setDelReason(""); } }}>
        <AlertDialogContent className="bg-[#0f2130] border-white/10 text-slate-100" data-testid="chat-confirm-delall">
          <AlertDialogHeader>
            <AlertDialogTitle className={`flex items-center gap-2 ${confirmDel?.kind === "leave" ? "text-amber-300" : "text-red-300"}`}>
              {confirmDel?.kind === "leave" ? <LogOut className="h-5 w-5" /> : <Ban className="h-5 w-5" />}
              {confirmDel?.kind === "leave" ? t("messagerie.leaveGroupTitle") : confirmDel?.kind === "conversation" ? t("messagerie.deleteConvForAllTitle") : t("messagerie.deleteMsgForAllTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirmDel?.kind === "leave" ? t("messagerie.leaveGroupDesc") : confirmDel?.kind === "conversation" ? t("messagerie.deleteConvForAllDesc") : t("messagerie.deleteMsgForAllDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmDel?.kind === "message" && confirmDel?.reasonNeeded && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">{t("messagerie.reasonLabel")}</label>
              <Textarea value={delReason} onChange={(e) => setDelReason(e.target.value)} data-testid="chat-del-reason"
                placeholder={t("messagerie.reasonPlaceholder")} className="bg-white/[0.04] border-white/10 text-slate-100 min-h-[70px]" />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="chat-del-cancel" className="bg-transparent border-white/15 text-slate-200 hover:bg-white/10">{t("messagerie.cancel2")}</AlertDialogCancel>
            <AlertDialogAction data-testid="chat-del-confirm" onClick={confirmDeleteForAll} className={confirmDel?.kind === "leave" ? "bg-amber-600 hover:bg-amber-500 text-white" : "bg-red-600 hover:bg-red-500 text-white"}>{confirmDel?.kind === "leave" ? t("messagerie.confirmLeave") : t("messagerie.confirmDelete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MsgActions({ align, onReact, onDelMe, onDelAll, canDelAll, t }) {
  return (
    <div className="flex items-center gap-0.5 mb-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition shrink-0">
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="chat-msg-react" title={t("messagerie.react")}
            className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-amber-300 hover:bg-white/10 transition">
            <Smile className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align={align} side="top" className="w-auto p-1.5 bg-[#0f2130] border-white/10" data-testid="chat-react-panel">
          <div className="flex gap-1">
            {QUICK_REACTIONS.map((e) => (
              <button key={e} type="button" data-testid={`chat-react-${e}`} onClick={() => onReact(e)}
                className="h-8 w-8 rounded-lg text-lg leading-none flex items-center justify-center hover:bg-white/10 transition">{e}</button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="chat-msg-menu" className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-white/10 transition">
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="bg-[#0f2130] border-white/10 text-slate-100">
          <DropdownMenuItem data-testid="chat-msg-del-me" onClick={onDelMe} className="cursor-pointer focus:bg-white/10">
            <Trash2 className="h-4 w-4 mr-2" />{t("messagerie.deleteForMe")}
          </DropdownMenuItem>
          {canDelAll && (
            <DropdownMenuItem data-testid="chat-msg-del-all" onClick={onDelAll} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300">
              <Ban className="h-4 w-4 mr-2" />{t("messagerie.deleteForEveryone")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

